import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Transition } from "react-transition-group";
import styled, { css } from "styled-components";
import { TransitionStatus } from "react-transition-group/Transition";
import { useActivation } from "lib/hooks/useActivation";

type Props<T extends HTMLElement> = {
  in: boolean;
  children: FC<{ container: React.RefObject<T>; onChildHeightChange: (event: "start" | "end") => void }>;
  onSlide?: (event: "start" | "end") => void;
};
const BottomSlide: <T extends HTMLElement>(p: Props<T>) => React.ReactElement<Props<T>> = <T extends HTMLElement>({
  in: inProp,
  children,
  onSlide,
}: Props<T>) => {
  const container = useRef<T>(null);
  const [containerHeight, setContainerHeight] = useState("0px");
  const [heightFree, heightFreeOn, heightFreeOff] = useActivation(false);
  const onChildHeightChange = useCallback((event: "start" | "end") => {
    if (event === "start") {
      heightFreeOn();
    } else if (event === "end") {
      setContainerHeight(`${container.current?.clientHeight || 0}px`);
      heightFreeOff();
    }
  }, []);
  const onSlideStart = useCallback(() => onSlide?.("start"), [onSlide]);
  const onSlideEnd = useCallback(() => onSlide?.("end"), [onSlide]);
  useEffect(() => {
    // unmountや非表示で閉じてしまう対策
    if (container.current?.clientHeight !== 0) {
      setContainerHeight(`${container.current?.clientHeight || 0}px`);
    }
  }, []);
  return (
    <Transition in={inProp} timeout={500} onExit={onSlideStart} onEnter={onSlideStart} onExited={onSlideEnd} onEntered={onSlideEnd}>
      {(state) => (
        <SlideContainer $heightFree={heightFree} $maxHeight={containerHeight} $state={state}>
          {children({ container, onChildHeightChange })}
        </SlideContainer>
      )}
    </Transition>
  );
};
export default BottomSlide;

const SlideContainer = styled.div<{ $state: TransitionStatus; $maxHeight: string; $heightFree: boolean }>`
  transition: max-height 160ms ease-in-out;
  height: auto;
  overflow: hidden;
  ${({ $state, $maxHeight, $heightFree }) => {
    if ($state === "entering") {
      return css`
        max-height: ${$maxHeight};
      `;
    } else if ($state === "entered") {
      return css`
        max-height: ${$heightFree ? "none" : $maxHeight};
      `;
    } else if ($state === "exiting") {
      return css`
        max-height: 0;
      `;
    } else if ($state === "exited") {
      return css`
        max-height: 0;
      `;
    }
  }}
`;
